import React, { useState } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import {
  Apple,
  Check,
  Circle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CommonButton from '../../common/components/CommonButton';
import CommonFormField from '../../common/components/CommonFormField';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { generateRandomString, validateEmail } from '../../common/const';

const LoginForm = ({ onSubmit, loading }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'rememberMe') {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      const error = validateField(name, value);
      if (!error) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      } else {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
  };

  const handleGoogleLogin = () => {
    // Construct Google OAuth URL
    const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    // Define parameters
    const params = {
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: `${process.env.REACT_APP_BASE_URL}/google/callback`,
      response_type: 'code',
      scope: [
        'openid',
        'email',
        'profile'
      ].join(' '),
      access_type: 'offline',
      prompt: 'consent',
      state: 'login',
      nonce: generateRandomString()
    };

    // Add parameters to URL
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        googleAuthUrl.searchParams.append(key, value);
      }
    });

    // Redirect to Google OAuth
    window.location.href = googleAuthUrl.toString();
  };

  const handleAppleLogin = () => {
    window.location.href = `${process.env.REACT_APP_APPLE_AUTH_URL}${new URLSearchParams({
      response_type: 'code id_token',
      response_mode: 'form_post', // or 'query' for GET request
      client_id: process.env.REACT_APP_APPLE_CLIENT_ID, // Your Service ID
      redirect_uri: `${process.env.REACT_APP_BASE_URL}/apple/callback`,
      scope: 'name email',
      nonce: generateRandomString(16),
      state: 'login'
    })}`;
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
      }}
    >
      {loading && (
        <LoadingSpinner />
      )}
      <Box sx={{
        width: '100%',
        maxWidth: {
          sm: '400px',   // Small screens
          md: '400px',
          lg: '500px'
        }
      }}>
        {/* Header */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px' }}>
            Welcome To
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: '24px' }}>
            Dream College Path
          </Typography>
          <Box sx={{ m: 'auto 90px' }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 400, color: 'text.main' }}>
              Log in to explore new opportunities, track your growth, and take the next step toward your goals.
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 1 }}>
            <CommonFormField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Please enter your email"
              error={errors.email}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 1 }}>
            <CommonFormField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.password}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(prev => !prev)}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  color="secondary"
                  size="small"
                  icon={<Circle sx={{ fontSize: 20, color: theme.palette.background.light }} />}
                  checkedIcon={
                    <Box sx={{ background: theme.palette.secondary.main, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                      <Check sx={{ color: 'white', fontSize: 12 }} />
                    </Box>
                  }
                  sx={{
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                      borderRadius: '50%',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '14px' }}>
                  Remember this Device
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Link href="/forgot-password" variant="body2" color="secondary.main" sx={{ fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>
              Forgot Password?
            </Link>
          </Box>
          <CommonButton
            label="Sign In"
            size="large"
            type="submit"
            disabled={loading}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleLogin}
                startIcon={
                  <Box
                    component="img"
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    sx={{ width: 20, height: 20 }}
                  />
                }
                sx={{
                  height: '40px',
                  color: 'black',
                  border: '1px solid lightgray',
                  '&:hover': {
                    borderColor: 'lightgray',
                    backgroundColor: 'white',
                  },
                }}
              >
                Google
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAppleLogin}
                startIcon={
                  <Apple />
                }
                sx={{
                  height: '40px',
                  borderColor: 'black',
                  color: 'white',
                  backgroundColor: 'black',
                  '&:hover': {
                    borderColor: 'black',
                    backgroundColor: 'black',
                  },
                }}
              >
                Apple
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don&apos;t have an account?{' '}
              <Link href="/register" variant="body2" color="secondary.main" sx={{ fontWeight: 200, textDecoration: 'none' }}>
                Sign Up
              </Link>
            </Typography>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default LoginForm;